// Ink drawing module: pressure-sensitive canvas modal + Quill blot.
// Saves drawings as SVG attachments with strokes JSON embedded in <metadata>
// so they can be re-edited later.

import Quill from 'quill';
import { getStroke } from 'perfect-freehand';
import { acceptsInk, inkPoint, inkSamples, inkRenderer, wireFingerDrawing, finishInkStroke, inkStrokeHits } from './ink-input';

/* eslint-disable @typescript-eslint/no-explicit-any */
const BlockEmbed = Quill.import('blots/block/embed') as any;

export interface InkDrawingValue {
  attachmentId: string;
  blobHash: string;
  filename: string;
}

export class InkDrawingBlot extends BlockEmbed {
  static blotName = 'ink-drawing';
  static tagName = 'div';
  static className = 'ql-ink-drawing';

  static create(value: InkDrawingValue): HTMLElement {
    const node = super.create() as HTMLElement;
    node.setAttribute('data-attachment-id', value.attachmentId || '');
    node.setAttribute('data-blob-hash', value.blobHash);
    node.setAttribute('data-filename', value.filename || 'drawing.svg');
    node.setAttribute('contenteditable', 'false');
    node.classList.add('ink-drawing-wrapper');

    const img = document.createElement('img');
    img.alt = 'Drawing';
    img.className = 'ink-drawing-img';
    img.src = `/api/attachments/blob/${encodeURIComponent(value.blobHash)}`;
    img.draggable = false;
    node.appendChild(img);

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'ink-edit-btn';
    editBtn.title = 'Edit drawing';
    editBtn.textContent = '✏️ Edit';
    editBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const detail = {
        node,
        attachmentId: node.getAttribute('data-attachment-id') || '',
        blobHash: node.getAttribute('data-blob-hash') || '',
      };
      window.dispatchEvent(new CustomEvent('ink-edit-request', { detail }));
    });
    node.appendChild(editBtn);

    return node;
  }

  static value(node: HTMLElement): InkDrawingValue {
    return {
      attachmentId: node.getAttribute('data-attachment-id') || '',
      blobHash: node.getAttribute('data-blob-hash') || '',
      filename: node.getAttribute('data-filename') || 'drawing.svg',
    };
  }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

// ----- Stroke types -----
type Pt = [number, number, number]; // x, y, pressure

interface Stroke {
  points: Pt[];
  color: string;
  size: number;
}

// ----- SVG helpers -----
function getSvgPathFromStroke(points: number[][]): string {
  if (points.length === 0) return '';
  const first = points[0]!;
  const d: Array<string | number> = ['M', first[0]!, first[1]!, 'Q'];
  for (let i = 0; i < points.length; i++) {
    const [x0, y0] = points[i]!;
    const [x1, y1] = points[(i + 1) % points.length]!;
    d.push(x0!, y0!, (x0! + x1!) / 2, (y0! + y1!) / 2);
  }
  d.push('Z');
  return d.join(' ');
}

function strokePathD(stroke: Stroke): string {
  const outline = getStroke(stroke.points, {
    size: stroke.size,
    thinning: 0.55,
    smoothing: 0.5,
    streamline: 0.5,
    simulatePressure: false,
  });
  return getSvgPathFromStroke(outline as number[][]);
}

// ----- Modal state -----
interface ModalState {
  open: boolean;
  strokes: Stroke[];
  redoStack: Stroke[];
  current: Stroke | null;
  tool: 'pen' | 'eraser';
  color: string;
  size: number;
  width: number;
  height: number;
  activePointerId: number | null;
  activePenType: string | null;
  onSave: ((svg: string) => Promise<void> | void) | null;
  onCancel: (() => void) | null;
}

const modal: ModalState = {
  open: false,
  strokes: [],
  redoStack: [],
  current: null,
  tool: 'pen',
  color: '#1a1b26',
  size: 4,
  width: 0,
  height: 0,
  activePointerId: null,
  activePenType: null,
  onSave: null,
  onCancel: null,
};
let saving = false;
let changed = false;
let loadFailed = false;
let openFrame = 0;
let confirmDiscard: (() => Promise<boolean>) | null = null;

const $ = (id: string) => document.getElementById(id) as HTMLElement;

const rerenderSvg = inkRenderer('ink-svg', strokePathD, () => modal);

function pointFromEvent(e: PointerEvent, svg: SVGSVGElement): Pt {
  return inkPoint(e, svg);
}

function eraseAt(x: number, y: number) {
  const radius = Math.max(modal.size * 1.5, 12);
  const before = modal.strokes.length;
  modal.strokes = modal.strokes.filter((s) => !inkStrokeHits(s, x, y, radius));
  if (modal.strokes.length !== before) {
    modal.redoStack = [];
    rerenderSvg();
  }
}

function onPointerDown(e: PointerEvent) {
  if (saving || loadFailed || !acceptsInk(e)) return;
  changed = true;
  const svg = $('ink-svg') as unknown as SVGSVGElement;

  if (modal.activePointerId !== null && modal.activePointerId !== e.pointerId) {
    // Pen-priority palm rejection: a pen event supersedes an in-progress non-pen stroke.
    if (e.pointerType === 'pen' && modal.activePenType !== 'pen') {
      svg.releasePointerCapture(modal.activePointerId);
      modal.current = null;
      modal.activePointerId = null;
      modal.activePenType = null;
      // Fall through to start the pen stroke.
    } else {
      return;
    }
  }

  e.preventDefault();

  svg.setPointerCapture(e.pointerId);
  modal.activePointerId = e.pointerId;
  modal.activePenType = e.pointerType;

  const [x, y, p] = pointFromEvent(e, svg);
  if (modal.tool === 'eraser') {
    eraseAt(x, y);
    return;
  }
  modal.current = {
    points: [[x, y, p]],
    color: modal.color,
    size: modal.size,
  };
  modal.redoStack = [];
  rerenderSvg();
}

function onPointerMove(e: PointerEvent) {
  if (modal.activePointerId !== e.pointerId) return;
  const svg = $('ink-svg') as unknown as SVGSVGElement;
  for (const sample of inkSamples(e)) {
    const point = pointFromEvent(sample, svg);
    if (modal.tool === 'eraser') eraseAt(point[0], point[1]);
    else modal.current?.points.push(point);
  }
  rerenderSvg();
}

function onPointerUp(e: PointerEvent) {
  if (modal.activePointerId !== e.pointerId) return;
  modal.activePointerId = null;
  modal.activePenType = null;

  if (modal.current && modal.current.points.length > 0) {
    finishInkStroke(modal.current, e, $('ink-svg') as unknown as SVGSVGElement);
    modal.strokes.push(modal.current);
  }
  modal.current = null;
  rerenderSvg();

}

function setTool(tool: 'pen' | 'eraser') {
  modal.tool = tool;
  document.querySelectorAll('[data-ink-tool]').forEach((el) => {
    const t = (el as HTMLElement).dataset.inkTool;
    el.classList.toggle('active', t === tool);
  });
  ($('ink-canvas-wrap') as HTMLElement).dataset.tool = tool;
}

function setColor(color: string) {
  modal.color = color;
  modal.tool = 'pen';
  document.querySelectorAll('[data-ink-color]').forEach((el) => {
    const c = (el as HTMLElement).dataset.inkColor;
    el.classList.toggle('active', c === color);
  });
  document.querySelectorAll('[data-ink-tool]').forEach((el) => {
    const t = (el as HTMLElement).dataset.inkTool;
    el.classList.toggle('active', t === 'pen');
  });
  ($('ink-canvas-wrap') as HTMLElement).dataset.tool = 'pen';
}

function undo() {
  if (saving) return;
  const last = modal.strokes.pop();
  if (last) {
    changed = true;
    modal.redoStack.push(last);
    rerenderSvg();
  }
}

function redo() {
  if (saving) return;
  const r = modal.redoStack.pop();
  if (r) {
    changed = true;
    modal.strokes.push(r);
    rerenderSvg();
  }
}

function clearAll() {
  if (saving) return;
  if (modal.strokes.length === 0 && !modal.current) return;
  changed = true;
  modal.redoStack = [...modal.strokes].reverse().concat(modal.redoStack);
  modal.strokes = [];
  modal.current = null;
  rerenderSvg();
}

function buildSavedSvg(): string {
  // Compute bbox so the saved SVG crops to content (with 16px padding).
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const s of modal.strokes) {
    const r = Math.max(s.size, 1);
    for (const [x, y] of s.points) {
      if (x - r < minX) minX = x - r;
      if (y - r < minY) minY = y - r;
      if (x + r > maxX) maxX = x + r;
      if (y + r > maxY) maxY = y + r;
    }
  }
  const pad = 16;
  minX -= pad;
  minY -= pad;
  maxX += pad;
  maxY += pad;
  if (!modal.strokes.length) {
    minX = minY = 0;
    maxX = modal.width; maxY = modal.height;
  }
  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);

  const paths = modal.strokes
    .map((s) => `<path d="${strokePathD(s)}" fill="${s.color}"/>`)
    .join('');

  const meta = JSON.stringify({
    v: 1,
    w: modal.width,
    h: modal.height,
    strokes: modal.strokes,
  });
  // Escape ]]> just in case.
  const safeMeta = meta.replace(/]]>/g, ']]]]><![CDATA[>');

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${minX} ${minY} ${w} ${h}" width="${w}" height="${h}">`
    + `<metadata id="swatnotes-ink"><![CDATA[${safeMeta}]]></metadata>`
    + paths
    + `</svg>`;
}

function validStroke(value: unknown): value is Stroke {
  if (!value || typeof value !== 'object') return false;
  const stroke = value as Partial<Stroke>;
  return typeof stroke.color === 'string' && CSS.supports('color', stroke.color)
    && !/[<>&"']/.test(stroke.color)
    && typeof stroke.size === 'number' && Number.isFinite(stroke.size) && stroke.size > 0
    && Array.isArray(stroke.points) && stroke.points.length > 0
    && stroke.points.every(point => Array.isArray(point) && point.length === 3
      && point.every(Number.isFinite) && point[2] >= 0 && point[2] <= 1);
}

function parseSavedSvg(svgText: string): { strokes: Stroke[]; width: number; height: number } | null {
  try {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror') || doc.documentElement.localName !== 'svg') return null;
    const meta = doc.getElementById('swatnotes-ink');
    if (!meta) {
      // Earlier versions saved cleared drawings as a bare, empty SVG.
      if (doc.documentElement.children.length) return null;
      const bounds = doc.documentElement.getAttribute('viewBox')?.trim().split(/\s+/).map(Number);
      if (!bounds || bounds.length !== 4 || !bounds.every(Number.isFinite) || bounds[2]! <= 0 || bounds[3]! <= 0) return null;
      return { strokes: [], width: bounds[2]!, height: bounds[3]! };
    }
    const txt = meta.textContent || '';
    const data = JSON.parse(txt);
    if (!data || data.v !== 1 || !Array.isArray(data.strokes) || !data.strokes.every(validStroke)
      || !Number.isFinite(data.w) || data.w <= 0 || !Number.isFinite(data.h) || data.h <= 0) return null;
    return {
      strokes: data.strokes as Stroke[],
      width: data.w,
      height: data.h,
    };
  } catch {
    return null;
  }
}

function onModalKeyDown(e: KeyboardEvent) {
  if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
  e.preventDefault();
  if (e.shiftKey) { redo(); } else { undo(); }
}

function sizeCanvas() {
  const wrap = $('ink-canvas-wrap');
  const rect = wrap.getBoundingClientRect();
  modal.width = Math.max(modal.width, 320, Math.round(rect.width));
  modal.height = Math.max(modal.height, 240, Math.round(rect.height));
  const svg = $('ink-svg') as unknown as SVGSVGElement;
  svg.setAttribute('viewBox', `0 0 ${modal.width} ${modal.height}`);
  svg.setAttribute('width', String(modal.width));
  svg.setAttribute('height', String(modal.height));
}

export interface OpenInkOptions {
  existingSvg?: string;
  onSave: (svg: string) => Promise<void> | void;
  onCancel?: () => void;
}

export function openInkModal(opts: OpenInkOptions) {
  const root = $('ink-modal');
  root.classList.remove('hidden');
  modal.open = true;
  saving = false; changed = false; loadFailed = false;
  ($('ink-save') as HTMLButtonElement).disabled = false;
  ($('ink-cancel') as HTMLButtonElement).disabled = false;
  $('ink-save').textContent = 'Save drawing';
  $('ink-error').textContent = '';
  modal.width = 0; modal.height = 0;
  modal.onSave = opts.onSave;
  modal.onCancel = opts.onCancel || null;
  modal.strokes = [];
  modal.redoStack = [];
  modal.current = null;
  modal.activePointerId = null;
  modal.activePenType = null;
  setTool('pen');
  setColor(modal.color);
  document.addEventListener('keydown', onModalKeyDown);

  if (opts.existingSvg !== undefined) {
    const parsed = parseSavedSvg(opts.existingSvg);
    if (parsed) {
      modal.width = parsed.width;
      modal.height = parsed.height;
      modal.strokes = parsed.strokes;
    } else {
      loadFailed = true;
      ($('ink-save') as HTMLButtonElement).disabled = true;
      $('ink-error').textContent = 'This drawing cannot be edited because its stroke data is missing or invalid. The original is unchanged.';
    }
  }
  // Wait for layout, then size canvas. Cancel stale callbacks on close/reopen.
  cancelAnimationFrame(openFrame);
  openFrame = requestAnimationFrame(() => {
    openFrame = 0;
    sizeCanvas();
    rerenderSvg();
  });
}

export function closeInkModal() {
  cancelAnimationFrame(openFrame);
  openFrame = 0;
  const root = $('ink-modal');
  root.classList.add('hidden');
  modal.open = false;
  modal.strokes = [];
  modal.redoStack = [];
  modal.current = null;
  modal.activePointerId = null;
  modal.activePenType = null;
  rerenderSvg();
  modal.onSave = null;
  modal.onCancel = null;
  document.removeEventListener('keydown', onModalKeyDown);
}

export function isInkModalOpen(): boolean {
  return modal.open;
}

export async function dismissInkModal(): Promise<boolean> {
  if (!modal.open) return true;
  if (saving) return false;
  if (changed && !await confirmDiscard?.()) return false;
  const cb = modal.onCancel;
  closeInkModal();
  if (cb) cb();
  return true;
}

export function wireInkModal(onConfirmDiscard: () => Promise<boolean>) {
  confirmDiscard = onConfirmDiscard;
  wireFingerDrawing();
  const svg = $('ink-svg') as unknown as SVGSVGElement;
  svg.addEventListener('pointerdown', onPointerDown);
  svg.addEventListener('pointermove', onPointerMove);
  svg.addEventListener('pointerup', onPointerUp);
  svg.addEventListener('pointercancel', onPointerUp);
  svg.addEventListener('lostpointercapture', onPointerUp);
  // Stop touch from scrolling the page while drawing.
  svg.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
  svg.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });

  document.querySelectorAll('[data-ink-color]').forEach((el) => {
    el.addEventListener('click', () => {
      const c = (el as HTMLElement).dataset.inkColor!;
      setColor(c);
    });
  });
  document.querySelectorAll('[data-ink-tool]').forEach((el) => {
    el.addEventListener('click', () => {
      const t = (el as HTMLElement).dataset.inkTool as 'pen' | 'eraser';
      setTool(t);
    });
  });
  ($('ink-size') as HTMLSelectElement).addEventListener('change', (e) => {
    modal.size = parseFloat((e.target as HTMLSelectElement).value) || 4;
  });
  $('ink-undo').addEventListener('click', undo);
  $('ink-redo').addEventListener('click', redo);
  $('ink-clear').addEventListener('click', clearAll);
  $('ink-cancel').addEventListener('click', () => dismissInkModal());
  $('ink-save').addEventListener('click', async () => {
    if (saving || loadFailed) return;
    const cb = modal.onSave;
    if (!cb) { closeInkModal(); return; }
    if (modal.current) { modal.strokes.push(modal.current); modal.current = null; }
    modal.activePointerId = null;
    const svgText = buildSavedSvg();
    const saveBtn = $('ink-save') as HTMLButtonElement;
    const cancelBtn = $('ink-cancel') as HTMLButtonElement;
    const prevSaveLabel = saveBtn.textContent;
    saving = true;
    saveBtn.disabled = true;
    cancelBtn.disabled = true;
    saveBtn.textContent = 'Saving…';
    try {
      // Callback is expected to throw on failure; if it does, we keep the
      // modal open so the user can retry instead of losing their drawing.
      await cb(svgText);
      closeInkModal();
    } catch (e) {
      $('ink-error').textContent = `Drawing not saved: ${(e as Error).message}. Your ink is still here; retry saving.`;
      saveBtn.textContent = prevSaveLabel || 'Save drawing';
      saveBtn.disabled = false;
      cancelBtn.disabled = false;
    } finally {
      saving = false;
    }
  });

  window.addEventListener('resize', () => {
    if (!modal.open) return;
    // Don't rescale strokes on resize; just resize canvas viewport.
    sizeCanvas();
    rerenderSvg();
  });
}

Quill.register({ 'formats/ink-drawing': InkDrawingBlot }, true);
